import React, { createRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import { Div } from '../../layouts';
import { DateUtils, TrackingUtils, withPageContext, ABUtils, Config } from '../../utils';
import { Calendar } from '../Calendar';
import { ConnectedFaresLoader } from '../Calendar/FaresLoader';
import { CalendarUtils } from '../../utils/CalendarUtils';
import { CalendarTypeProp, CalendarTypePropDefault, EntryPoint, TabTypeProp, TabTypePropDefault } from '../../models';
import { FaresCalendarLegend } from '../Calendar/Context/FaresCalendarLegend';
import { MonthContext } from './MonthContext';

const today = DateUtils.getToday();
const EXTRA_ITEM_HEIGHT = 32;
const LEGEND_HEIGHT = 75;

const Row = ({ index, style, data }) => {
    const { isCssVariationMobile } = ABUtils.getABs();
    const {
        dates,
        displayFaresInCalendar,
        isDatesRangeShown,
        maxDate,
        minDate,
        onChange,
        type,
        value,
    } = data;
    const parsedDates = dates.map((date) => DateUtils.stringToDate(date));
    const parsedMaxDate = DateUtils.stringToDate(maxDate);
    const parsedMinDate = DateUtils.stringToDate(minDate);
    const currentMonth = DateUtils.addMonths(parsedMinDate, index);
    const diff = DateUtils.getDifferenceInDays(currentMonth, parsedMinDate);
    const FLEXIBLE_DAYS = Config.get('daysWithFares');
    const flexibleDate =
        diff >= FLEXIBLE_DAYS
            ? DateUtils.add(parsedMinDate, Math.floor(diff / FLEXIBLE_DAYS) * FLEXIBLE_DAYS)
            : parsedMinDate;
    const displayMonth = DateUtils.addMonths(DateUtils.getFirstDayOfMonth(today), index);
    const monthClass = `month-${index}`;
    const [monthVisible, setMonthVisible] = useState(false);

    const wrapperClassName = classNames({
        'odf-space-outer-left-m odf-space-outer-right-m': !isCssVariationMobile,
        'prisma-responsive-space-horizontal odf-space-inner-top-s odf-space-inner-bottom-s': isCssVariationMobile,
        'calendar-wrapper': true,
    });
    const wrapperLegendsClassName = classNames({
        'odf-space-outer-top-s': !isCssVariationMobile,
    });

    useEffect(() => {
        if (window.IntersectionObserver) {
            const options = {
                root: document.querySelector('.calendar_intersection'),
                rootMargin: '0px',
                threshold: 0,
            };

            const callback = (entries) => {
                if (entries && entries.length) {
                    setMonthVisible(entries[0].isIntersecting);
                }
            };
            const observer = new IntersectionObserver(callback, options);
            const target = document.querySelector(`.${monthClass}`);
            observer.observe(target);
            return () => observer.unobserve(target);
        } else {
            setMonthVisible(true);
        }
    }, [monthClass]);

    const cachedMonthVisible = useMemo(() => ({ visible: monthVisible }), [monthVisible]);

    return (
        <Div className={monthClass} {...style}>
            {displayFaresInCalendar && index > 0 && (
                <div className="odf-stroke-primary odf-space-outer-bottom-m" />
            )}
            <div className={wrapperClassName}>
                <ConnectedFaresLoader date={flexibleDate}>
                    <MonthContext.Provider value={cachedMonthVisible}>
                        <Calendar
                            displayMonth={displayMonth}
                            dates={parsedDates}
                            isDatesRangeShown={isDatesRangeShown}
                            onSelect={onChange}
                            minDate={parsedMinDate}
                            maxDate={parsedMaxDate}
                            selectedDate={value}
                            type={type}
                        />
                    </MonthContext.Provider>
                </ConnectedFaresLoader>
            </div>
            {displayFaresInCalendar && (
                <div className={wrapperLegendsClassName}>
                    <FaresCalendarLegend extraMarginLeft />
                </div>
            )}
        </Div>
    );
};

Row.propTypes = {
    index: PropTypes.number.isRequired,
    data: PropTypes.object.isRequired,
    style: PropTypes.object.isRequired,
};

const DatePickerVertical = ({
    width,
    height,
    dates,
    value,
    onChange,
    isDatesRangeShown,
    minDate,
    maxDate,
    type,
    displayFaresInCalendar,
    trackEvent,
    activeProduct,
    direction,
}) => {
    const { t } = useTranslation();
    const listRef = createRef();

    const getItemSize = (index) => {
        const month = DateUtils.addMonths(today, index);
        const extraHeight = displayFaresInCalendar ? LEGEND_HEIGHT : EXTRA_ITEM_HEIGHT;
        return CalendarUtils.getCalendarHeight(month, t('calendar.startDay', Number)) + extraHeight;
    };

    useEffect(() => {
        const index = DateUtils.getDifferenceInMonths(today, DateUtils.stringToDate(value) || DateUtils.stringToDate(minDate));
        listRef.current.scrollToItem(index, 'center');
    }, [minDate, value, listRef]);

    useEffect(() => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        if (activeProduct === EntryPoint.HOTELS) {
            const label = TrackingUtils.LABEL.fieldOpen(activeProduct, 'calendar');
            trackEvent({ action, label });
        } else {
            trackEvent({ action, label: 'open_calendar' });
        }
    }, [activeProduct]);

    const rowData = {
        dates,
        minDate,
        maxDate,
        isDatesRangeShown,
        onChange,
        type,
        value,
        displayFaresInCalendar,
    };

    return (
        <List
            className="calendar_intersection"
            ref={listRef}
            height={height}
            itemCount={13}
            itemData={rowData}
            itemSize={getItemSize}
            width={width}
            overscanCount={1}
            direction={direction}
        >
            {Row}
        </List>
    );
};

const withSize = (Child) => (props) => (
    <AutoSizer>
        {({ width, height }) => <Child width={width} height={height} {...props} />}
    </AutoSizer>
);

DatePickerVertical.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    dates: PropTypes.arrayOf(Date),
    displayFaresInCalendar: PropTypes.bool.isRequired,
    value: PropTypes.instanceOf(Date),
    isDatesRangeShown: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    type: CalendarTypeProp,
    trackEvent: PropTypes.func.isRequired,
    activeProduct: TabTypeProp,
    direction: PropTypes.string,
};

DatePickerVertical.defaultProps = {
    dates: [],
    value: null,
    isDatesRangeShown: false,
    minDate: Calendar.defaultProps.minDate,
    maxDate: Calendar.defaultProps.maxDate,
    onChange: () => {},
    type: CalendarTypePropDefault,
    activeProduct: TabTypePropDefault,
    direction: 'ltr',
};

const mapStateToProps = ({ activeProduct }) => ({
    activeProduct: activeProduct.activeProduct,
});

const ConnectedDatePickerVertical = connect(
    mapStateToProps,
    {},
)(withPageContext(withSize(DatePickerVertical)));
export { ConnectedDatePickerVertical as DatePickerVertical };
